import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCaretUp, FaCaretDown } from "react-icons/fa";
import { getUrlParams } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./LifestyleCard.css";

// Keeps hue + saturation from backend color but clamps lightness for readability.
// Dark mode: push lightness up (≥70%) so it shows on dark bg.
// Light mode: push lightness down (≤45%) so it shows on light bg.
const adjustColorForTheme = (color, isDark) => {
  if (!color) return isDark ? "#ffffff" : "#343434";
  const hex = color.replace("#", "");
  const full = hex.length === 3
    ? hex.split("").map(c => c + c).join("")
    : hex;
  if (full.length !== 6) return isDark ? "#ffffff" : "#343434";

  let r = parseInt(full.slice(0, 2), 16) / 255;
  let g = parseInt(full.slice(2, 4), 16) / 255;
  let b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  l = isDark ? Math.max(l, 0.70) : Math.min(l, 0.45);
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

// ── shimmer ───────────────────────────────────────────────────────────────────
const shimmerCSS = `@keyframes lc-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}`;
const _el = document.createElement("style");
_el.innerText = shimmerCSS;
document.head.appendChild(_el);

const shimmer = {
  background: "linear-gradient(90deg,var(--shimmer-1,#e0e0e0) 0%,var(--shimmer-2,#f5f5f5) 50%,var(--shimmer-1,#e0e0e0) 100%)",
  backgroundSize: "200% 100%",
  animation: "lc-shimmer 1.5s infinite",
};

// ── CDN maps (matched from Angular CSS) ──────────────────────────────────────

const CARD_BG = {
  sedentary: "https://appcdn.goqii.com/storeimg/67997_1777963019.png",
  active:    "https://appcdn.goqii.com/storeimg/19663_1777963042.png",
  fit:       "https://appcdn.goqii.com/storeimg/23800_1777963062.png",
  elite:     "https://appcdn.goqii.com/storeimg/50360_1777963086.png",
};

const CARD_IMG = {
  sedentary: "https://appcdn.goqii.com/storeimg/80701_1778137496.png",
  active:    "https://appcdn.goqii.com/storeimg/99458_1778137525.png",
  fit:       "https://appcdn.goqii.com/storeimg/29870_1778137542.png",
  elite:     "https://appcdn.goqii.com/storeimg/89683_1778137557.png",
};
// const CARD_IMG = {
//   sedentary: "https://appcdn.goqii.com/storeimg/98926_1777973757.png",
//   active:    "https://appcdn.goqii.com/storeimg/39626_1777973736.png",
//   fit:       "https://appcdn.goqii.com/storeimg/87559_1777973716.png",
//   elite:     "https://appcdn.goqii.com/storeimg/60905_1777973693.png",
// };
// const CARD_IMG = {
//   sedentary: "https://appcdn.goqii.com/storeimg/96765_1652346729.png",
//   active:    "https://appcdn.goqii.com/storeimg/94257_1652346397.png",
//   fit:       "https://appcdn.goqii.com/storeimg/94897_1652347236.png",
//   elite:     "https://appcdn.goqii.com/storeimg/2752_1652254998.png",
// };

const RING_COLOR = {
  sedentary: "#F76E66",
  active:    "#F8C031",
  fit:       "#56B76F",
  elite:     "#4A77C0",
};

const AGE_COLOR = {
  "age-tag-red":   "#F76E66",
  "age-tag-green": "#57C063",
};

// ── skeleton ──────────────────────────────────────────────────────────────────
const Skeleton = () => (
  <div className="lc-root-container" style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 12px" }}>
    <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
      <div style={{ width:72, height:72, borderRadius:"50%", ...shimmer }} />
      <div style={{ width:56, height:8, borderRadius:4, ...shimmer }} />
    </div>
    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8 }}>
      <div style={{ height:15, width:"55%", borderRadius:6, ...shimmer }} />
      <div style={{ height:11, width:"75%", borderRadius:5, ...shimmer }} />
      <div style={{ height:11, width:"60%", borderRadius:5, ...shimmer }} />
    </div>
    <div style={{ flexShrink:0, width:70, height:90, borderRadius:10, ...shimmer }} />
  </div>
);

// ── component ─────────────────────────────────────────────────────────────────
const LifestyleCard = () => {
  const { t }                     = useTranslation();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) { setError(t("lifestyleCard.error")); setLoading(false); return; }

    lifestyleApi.lifestyleEliteCard(encryptedId)
      .then(res => {
        const d = res?.data?.data;
        d ? setData(d) : setError(t("lifestyleCard.error"));
      })
      .catch(() => setError(t("lifestyleCard.error")))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton />;
  if (error || !data) return (
    <div className="lc-root-container" style={{ display:"flex", justifyContent:"center", alignItems:"center", minHeight:90, color:"var(--text-muted)", fontSize:13 }}>
      {error || t("lifestyleCard.error")}
    </div>
  );

  const { theme } = getUrlParams();
  const isDark  = theme === "dark";

  const sub      = data.subclass || "sedentary";
  const cardBg   = CARD_BG[sub]   ?? CARD_BG.sedentary;
  const cardImg  = CARD_IMG[sub]  ?? CARD_IMG.sedentary;
  const ring     = RING_COLOR[sub] ?? RING_COLOR.sedentary;
  const ageColor = AGE_COLOR[data.agetag] ?? "var(--text-secondary)";
  const isUp     = data.caret === "fa-caret-up";
  const healthTextColor = adjustColorForTheme(data.textColor, isDark);

  return (
    <div className="lc-root-container">

      {/* ── BG image layer — .lc-bg-img sets opacity:1 (light) → 0.70 (dark) ── */}
      <div className="lc-bg-img" style={{ backgroundImage:`url(${cardBg})` }} />

      {/* ── Content (above bg) ── */}
      <a
        href={data.difference ? data.cardurl : undefined}
        style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 12px", position:"relative", zIndex:1, textDecoration:"none", color:"inherit" }}
      >

        {/* ── LEFT: circular avatar + badge overlay + "I am Protected" ── */}
        <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center" }}>
          <a href={data.url} onClick={e => e.stopPropagation()} style={{ textDecoration:"none", display:"block", position:"relative" }}>

            {/* avatar — circle with lifestyle-colour ring */}
            <img
              src={data.userImage}
              alt={data.name}
              style={{
                width: 72, height: 72,
                borderRadius: "50%",
                border: `3px solid ${ring}`,
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                backgroundColor: "#fff",
              }}
            />

            {/* badge — overlaid bottom-centre of avatar */}
            {data.showBadge && data.badge && (
              <img
                src={data.badge}
                alt="badge"
                style={{ position:"absolute", width:25, height:25, bottom:0, left:"20%", transform:"translateX(-50%)", zIndex:2 }}
              />
            )}
          </a>

          {/* "I am Protected" text under avatar */}
          {data.showBadge && (
            <span className="lc-protected-label">I am Protected</span>
          )}
        </div>

        {/* ── MIDDLE: lifestyle_img icon + name · age · health text ── */}
        <div style={{ flex:1, minWidth:0, paddingLeft:4 }}>

          {/* row 1 — lifestyle icon + player name */}
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
            {/* {data.lifestyle_img && (
              <img src={data.lifestyle_img} alt="" style={{ height:22, width:"auto", objectFit:"contain", flexShrink:0 }} />
            )} */}
            <span
              style={{
                fontSize: "calc(10px + 1vw)",
                fontWeight: 700,
                color: "var(--text-primary)",
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {data.name}
              {data.dayCount >= 100 && (
                <img src="https://appcdn.goqii.com/storeimg/71355_1668668649.png" style={{ height:"1rem", marginLeft:4, verticalAlign:"middle" }} alt="" />
              )}
            </span>
          </div>

          {/* row 2 — GOQii Age : caret + bioage pill */}
          <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:4, marginBottom:5 }}>
            <span className="lc-age-label" style={{ fontSize:"calc(7px + 1vw)" }}>
              {t("lifestyleCard.goqiiAge")} :
            </span>
            {data.difference && (isUp
              ? <FaCaretUp  size={12} color={ageColor} />
              : <FaCaretDown size={12} color={ageColor} />
            )}
            {data.bioage && (
              <span style={{ backgroundColor:"#8058AC", color:"#fff", fontSize:11, fontWeight:700, borderRadius:5, paddingInline:6, paddingBlock:2, whiteSpace:"nowrap" }}>
                {data.bioage}
              </span>
            )}
          </div>

          {/* row 3 — health alert text */}
          {data.healthText && (
            <p className="lc-health-text" style={{ color: healthTextColor }}>
              {data.healthText}
            </p>
          )}
        </div>

        {/* ── RIGHT: subclass card image with day count text inside ── */}
        <div style={{ flexShrink:0, width:"24%", alignSelf:"stretch", display:"flex" }}>
          <div
            style={{
              backgroundImage: `url(${cardImg})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              width: "100%",
              minHeight: 90,
              position: "relative",
            }}
          >
            {/* centered in the inner badge area (left ~22% skips the SAFE side-blocks) */}
            <div style={{
              position: "absolute",
              bottom: "12%",
              left: "6%",
              right: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}>
              <div style={{ color:"#fff", fontSize:"calc(11px + 1vw)", fontWeight:700, lineHeight:0.7 }}>
                {data.dayCount}
              </div>
              <div style={{ color:"#fff", fontSize:"calc(5px + 1vw)", fontWeight:400, marginTop:0 }}>
                {data.dayString}
              </div>
            </div>
          </div>
        </div>

      </a>
    </div>
  );
};

export default LifestyleCard;
